import { AssociatedObjectsCardContext } from '../../context/AssociatedObjectsCardContext';
import { POLL_INTERVAL, UNKNOWN_ACTION, useFetchAssociatedObjectsCardAssociations } from '../../hooks/useFetchAssociatedObjectsCardAssociations';
import useFetchAssociationTypesData from '../../context/useFetchAssociationTypesData';
import { useFetchAssociatedObjectsCardObject } from '../../hooks/useFetchAssociatedObjectsCardObject';
import AssociatedObjectsCardTitle from './AssociatedObjectsCardTitle';
import { getImpliedUnlabeledAssociationDetails } from '../../hooks/useFetchAssociatedObjectsCardAssociations';
import { TOPIC_NAMES } from 'crm-message-bus/types/MessageTopics';
import { useOnCrmMessageTopic } from 'crm-message-bus/useOnCrmMessageTopic';
import Raven from 'raven-js';
import { useFetchAllAssociationTypesBetweenObjectIds } from '../../hooks/useFetchAllAssociationTypesBetweenObjectIds';
import { useEffect } from 'react';
import { jsx as _jsx } from "react/jsx-runtime";
function AssociatedObjectsCardTitleDataProvider({
  cardId,
  hasScope,
  isCollapsed,
  isEmbedded,
  isUngated,
  objectId,
  objectTypeId,
  onAssociationUpdate,
  onObjectCreate,
  onObjectUpdate,
  propertyNames,
  propertySource,
  readOnly,
  sourceApp,
  title,
  toObjectTypeId
}) {
  const associationTypesData = useFetchAssociationTypesData({
    fromObjectTypeId: objectTypeId,
    toObjectTypeId
  });
  const {
    associationTypes,
    unlabeledAssociationType,
    primaryAssociationType
  } = associationTypesData;
  const {
    impliedUnlabeledAssociationCategory,
    impliedUnlabeledInverseAssociationTypeId
  } = getImpliedUnlabeledAssociationDetails({
    associationTypes,
    unlabeledAssociationType
  });
  const {
    impliedUnlabeledAssociationObjects,
    primaryAssociationObject,
    refetch: refetchAssociationsData,
    startPolling: startPollingAssociationsData,
    onStopPolling: stopPollingAssociationsData,
    updateAssociationAction,
    client,
    associationsDataError
  } = useFetchAssociatedObjectsCardAssociations({
    impliedUnlabeledAssociationCategory,
    impliedUnlabeledInverseAssociationTypeId,
    fromObjectId: objectId,
    toObjectTypeId,
    primaryAssociationCategory: primaryAssociationType && primaryAssociationType.associationCategory,
    primaryInverseAssociationTypeId: primaryAssociationType ? primaryAssociationType.inverseAssociationTypeId : null
  });
  useEffect(() => {
    return () => {
      client.cache.evict({
        id: 'ROOT_QUERY',
        fieldName: 'crmObjectsSearch'
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const {
    objectTypePermissions,
    primaryDisplayLabel,
    refetch: refetchObjectData,
    userPermissions
  } = useFetchAssociatedObjectsCardObject({
    objectId: Number(objectId),
    objectType: objectTypeId,
    toObjectTypeId
  });
  const toObjectIds = impliedUnlabeledAssociationObjects ? impliedUnlabeledAssociationObjects.results.map(association => association.id) : [];
  const {
    refetch: refetchAllAssociationTypesBetweenObjectIdsData
  } = useFetchAllAssociationTypesBetweenObjectIds({
    fetchPolicy: 'cache-and-network',
    objectId,
    objectTypeId,
    skip: isCollapsed,
    toObjectIds,
    toObjectTypeId
  });
  useOnCrmMessageTopic(TOPIC_NAMES.UPDATE_ASSOCIATIONS, ({
    toObjectTypeId: messageToObjectTypeId,
    objectTypeId: messageObjectTypeId,
    objectId: messageObjectId,
    actionType
  }) => {
    const associatedObjectsIds = impliedUnlabeledAssociationObjects && impliedUnlabeledAssociationObjects.results.map(result => result.id) || [];
    const legacyObjectTypeIdLogic = toObjectTypeId === messageObjectTypeId;
    const correctObjectTypeIdLogic = messageObjectTypeId === objectTypeId && (!messageToObjectTypeId || toObjectTypeId === messageToObjectTypeId);
    if ((legacyObjectTypeIdLogic || correctObjectTypeIdLogic) && [objectId, ...associatedObjectsIds].includes(+messageObjectId) && !updateAssociationAction.current) {
      // Association labels are updated "instantly" so we can refetch them directly
      refetchAllAssociationTypesBetweenObjectIdsData().catch(error => {
        Raven.captureException(error);
        throw new Error('No data returned from refetchAllAssociationTypesBetweenObjectIdsData query');
      });
      if (actionType === 'UPDATE_LABELS') {
        return;
      }
      updateAssociationAction.current = actionType || UNKNOWN_ACTION;
      startPollingAssociationsData(POLL_INTERVAL);
      // Stop polling if after 6s to prevent infinite polling
      setTimeout(() => {
        if (updateAssociationAction.current) {
          stopPollingAssociationsData();
        }
      }, 6000);
    }
  });
  return /*#__PURE__*/_jsx(AssociatedObjectsCardContext.Provider, {
    value: {
      associationsData: {
        impliedUnlabeledAssociationObjects,
        primaryAssociationObject,
        associationsDataError
      },
      associationTypesData,
      cardId,
      hasScope,
      isEmbedded,
      isUngated,
      objectId,
      objectTypeId,
      objectTypePermissions,
      onAssociationUpdate,
      onObjectCreate,
      onObjectUpdate,
      primaryDisplayLabel,
      propertyNames,
      propertySource,
      readOnly,
      refetchAssociationsData,
      refetchObjectData,
      sourceApp,
      title,
      toObjectTypeId,
      userPermissions
    },
    children: /*#__PURE__*/_jsx(AssociatedObjectsCardTitle, {})
  });
}
export default AssociatedObjectsCardTitleDataProvider;